import { createPopper } from '@popperjs/core';

import { getSvgSelected, getSvgNonSelected } from 'utils/domElements';

const getDisabledPopoverContent = (checkbox, colId, rowId, node) => {
    const { disabledPopoverContent } = checkbox;
    if (!disabledPopoverContent) return null;
    if (typeof disabledPopoverContent === 'function') {
        return disabledPopoverContent(colId, rowId, node);
    }
    return disabledPopoverContent;
};

const attachDisabledPopover = (triggerEl, content, tableId) => {
    if (!content || !triggerEl) return;

    const root = tableId ? document.querySelector(`#${tableId}`) : document;
    const popover = root?.querySelector('.w5-popover');
    if (!popover) {
        triggerEl.setAttribute('title', content);
        return;
    }

    let popperInstance = null;

    const showPopover = () => {
        popover.style.display = 'block';
        popover.style.zIndex = '10000';
        if (popover.firstElementChild) {
            popover.firstElementChild.innerText = content;
        }
        popperInstance?.destroy();
        popperInstance = createPopper(triggerEl, popover, {
            placement: 'top',
            strategy: 'fixed',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 8],
                    },
                },
            ],
        });
    };

    const hidePopover = () => {
        popover.style.display = 'none';
        if (popperInstance) {
            popperInstance.destroy();
            popperInstance = null;
        }
    };

    // mouseover/out bubble from child SVG; mouseenter/leave do not.
    triggerEl.addEventListener('mouseover', () => {
        showPopover();
    });

    triggerEl.addEventListener('mouseout', (event) => {
        const { relatedTarget } = event;
        if (relatedTarget && triggerEl.contains(relatedTarget)) return;
        if (relatedTarget && popover.contains(relatedTarget)) return;
        hidePopover();
    });
};

export default function CellCheckbox(params) {
    const { checkbox, column, context } = params;
    if (!checkbox) return null;

    const rowId = params.node.id;
    if (!rowId) return null;

    const colId = column?.colId || '';
    let isDisabled = false;
    if (checkbox.isDisabled) {
        if (typeof checkbox.isDisabled === 'function')
            isDisabled = checkbox.isDisabled(colId, rowId, params.node);
        else if (checkbox.isDisabled) isDisabled = checkbox.isDisabled;
    }

    let isChecked = false;
    let isBlocked = isDisabled && checkbox.noCheckedWhenDisabled;
    if (context?.checkboxData?.[colId]?.rows) {
        isChecked = checkbox.getIsRowChecked(colId, rowId);
        if (isBlocked) isChecked = false;
    }
    const tableId = context?.tableId || '';

    const svg = getSvg(isChecked, tableId, colId, rowId);

    const cell = document.createElement('div');
    cell.classList.add('w5-cell-checkbox');
    if (isDisabled) cell.classList.add('w5-cell-checkbox--disabled');
    if (isBlocked) {
        cell.classList.add('w5-cell-checkbox--blocked');
    }
    cell.setAttribute('data-ischecked', isChecked);

    if (!svg) return cell;

    if (!params.checkbox.isAlwaysVisible) {
        if (isChecked) {
            cell.classList.remove('w5-cell-checkbox--hidden');
            cell.classList.add('w5-cell-checkbox--visible');
        } else {
            cell.classList.remove('w5-cell-checkbox--visible');
            cell.classList.add('w5-cell-checkbox--hidden');
        }
    }

    if (checkbox.onClick) {
        svg.addEventListener(
            'click',
            handleOnChange(
                cell,
                svg,
                rowId,
                isChecked,
                isDisabled,
                colId,
                checkbox.onClick,
                context,
                params.rowIndex,
                params.data,
                params.checkbox.isAlwaysVisible,
                tableId,
            ),
        );

        svg.addEventListener(
            'mousedown',
            (e) => (e.detail > 1 ? e.preventDefault() : false),
            false,
        );
    }

    cell.appendChild(svg);

    if (isDisabled) {
        const popoverContent = getDisabledPopoverContent(checkbox, colId, rowId, params.node);
        attachDisabledPopover(cell, popoverContent, tableId);
    }

    return cell;
}

const getSvg = (isChecked, tableId, colId, rowId) => {
    const svgContainer = document.createElement('div');
    svgContainer.classList.add('w5-cell-checkbox__svg-container');
    if (isChecked) {
        const svgSelected = getSvgSelected(tableId, colId, rowId);
        if (!svgSelected) return null;
        svgContainer.innerHTML = svgSelected;
    } else {
        const svgNonSelected = getSvgNonSelected(tableId, colId, rowId);
        if (!svgNonSelected) return null;
        svgContainer.innerHTML = svgNonSelected;
    }
    return svgContainer;
};

const handleOnChange =
    (
        cell,
        svg,
        rowId,
        value,
        isDisabled,
        colId,
        onClick,
        context,
        rowIndex,
        data,
        isAlwaysVisible,
        tableId,
    ) =>
    (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (isDisabled) return;
        let domValue = cell.getAttribute('data-ischecked');
        if (domValue) {
            domValue = checkIsTrue(domValue);
            value = !domValue;
            cell.setAttribute('data-ischecked', !domValue);
        } else value = !value;

        svg.remove();
        const newSvg = getSvg(value, tableId, colId, rowId);
        if (!newSvg) return null;

        newSvg.addEventListener(
            'click',
            handleOnChange(
                cell,
                newSvg,
                rowId,
                value,
                isDisabled,
                colId,
                onClick,
                context,
                rowIndex,
                data,
                isAlwaysVisible,
                tableId,
            ),
        );

        newSvg.addEventListener(
            'mousedown',
            (e) => (e.detail > 1 ? e.preventDefault() : false),
            false,
        );

        if (!isAlwaysVisible) {
            if (value) {
                cell.classList.remove('w5-cell-checkbox--hidden');
                cell.classList.add('w5-cell-checkbox--visible');
            } else {
                cell.classList.remove('w5-cell-checkbox--visible');
                cell.classList.add('w5-cell-checkbox--hidden');
            }
        }
        cell.appendChild(newSvg);

        onClick({ id: rowId, colId, boolean: value, context, rowIndex, data });
    };

const checkIsTrue = (value) => {
    if (!value || value === 'false') return false;
    return true;
};
